/**
 * @fileoverview Kaydedilen Ilanlar Hook'u
 *
 * Bu modul, kullanicinin favori ilanlarini ve projelerini yoneten
 * custom hook'u icerir. Kaydetme/kaldirma islemleri widget state'inde
 * tutulur ve ChatGPT ile senkronize edilir.
 *
 * @module useSavedListings
 */

import { useMemo, useCallback } from "react";

/** Maximum number of saved listings allowed */
const MAX_SAVED_LISTINGS = 5;

/**
 * @typedef {Object} UseSavedListingsReturn
 * @property {Set<string>} savedListingIdSet - Kaydedilen ilan ID'leri Set'i
 * @property {Set<string>} savedProjectIdSet - Kaydedilen proje ID'leri Set'i
 * @property {Function} toggleSaveListing - Ilan kaydetme/kaldirma toggle fonksiyonu
 * @property {Function} toggleSaveProject - Proje kaydetme/kaldirma toggle fonksiyonu
 * @property {boolean} isAtLimit - Maximum kayit limitine ulasildi mi
 * @property {number} savedCount - Kaydedilen ilan sayisi
 */

/**
 * Kaydedilen ilanlar ve projeler islevselligini yonetir.
 *
 * Bu hook, kullanicinin favori ilanlarini ve projelerini yonetir.
 * Veriler widget state'inde tutulur ve ChatGPT ile paylasilir, boylece
 * kullanici "kaydettiklerimi goster" gibi isteklerde bulunabilir.
 *
 * **State Yapisi:**
 * Kaydedilen ID'ler `modelContent.savedListingIds` ve
 * `modelContent.savedProjectIds` dizilerinde tutulur.
 *
 * **Performans:**
 * Array'ler Set'lere donusturulur (O(1) arama icin memoize edilir)
 *
 * **Toggle Mantigi:**
 * - ID zaten kaydedilmisse, kaldirilir
 * - ID kaydedilmemisse, eklenir
 *
 * @function useSavedListings
 * @param {string[]} savedListingIds - Kaydedilen ilan ID'leri dizisi
 * @param {string[]} savedProjectIds - Kaydedilen proje ID'leri dizisi
 * @param {Function} updateWidgetState - Widget state guncelleme fonksiyonu
 * @returns {UseSavedListingsReturn} Kaydedilen ilanlar islevleri
 *
 * @example
 * const {
 *   savedListingIdSet,
 *   toggleSaveListing,
 * } = useSavedListings(
 *   widgetState.modelContent.savedListingIds,
 *   widgetState.modelContent.savedProjectIds,
 *   updateWidgetState
 * );
 *
 * // Ilan kaydedilmis mi kontrol et
 * const isSaved = savedListingIdSet.has(listing.id);
 *
 * // Kaydetme butonuna tiklandiginda
 * <SaveButton
 *   isSaved={isSaved}
 *   onClick={() => toggleSaveListing(listing.id)}
 * />
 */
export function useSavedListings(
  savedListingIds,
  savedProjectIds,
  updateWidgetState
) {
  const savedListingIdSet = useMemo(
    () => new Set(savedListingIds),
    [savedListingIds]
  );
  const savedProjectIdSet = useMemo(
    () => new Set(savedProjectIds),
    [savedProjectIds]
  );

  const toggleSaveListing = useCallback(
    (id) => {
      updateWidgetState((state) => {
        const current = new Set(state.modelContent.savedListingIds);
        if (current.has(id)) {
          // Always allow removal
          current.delete(id);
        } else {
          // Check limit before adding
          if (current.size >= MAX_SAVED_LISTINGS) {
            // At limit, don't add
            return state;
          }
          current.add(id);
        }
        return {
          ...state,
          modelContent: {
            ...state.modelContent,
            savedListingIds: Array.from(current),
          },
        };
      });
    },
    [updateWidgetState]
  );

  const toggleSaveProject = useCallback(
    (projectId) => {
      if (!projectId) return;
      updateWidgetState((state) => {
        const current = new Set(state.modelContent.savedProjectIds);
        if (current.has(projectId)) {
          current.delete(projectId);
        } else {
          current.add(projectId);
        }
        return {
          ...state,
          modelContent: {
            ...state.modelContent,
            savedProjectIds: Array.from(current),
          },
        };
      });
    },
    [updateWidgetState]
  );

  const savedCount = savedListingIdSet.size;
  const isAtLimit = savedCount >= MAX_SAVED_LISTINGS;

  return {
    savedListingIdSet,
    savedProjectIdSet,
    toggleSaveListing,
    toggleSaveProject,
    savedCount,
    isAtLimit,
    maxSavedListings: MAX_SAVED_LISTINGS,
  };
}

export default useSavedListings;
